'use client';

import Link from 'next/link';
import { useRouter } from 'next/navigation';

const FEATURED_VOYAGES = [
  { name: 'Regent Seven Seas Splendor', route: 'Caribbean · Miami round-trip', nights: '10 nights', line: 'Regent Seven Seas', guests: '750 guests', price: 'From $9,800 per guest', tags: ['All-inclusive', 'All-suite', 'Caribbean'] },
  { name: 'Silversea Mediterranean Classics', route: 'Rome to Venice', nights: '10 nights', line: 'Silversea', guests: '596 guests', price: 'From $12,800 per guest', tags: ['Mediterranean', 'All-suite', 'Butler Service'] },
  { name: 'Cunard Transatlantic Grill Suites', route: 'New York to Southampton', nights: '7 nights', line: 'Cunard', guests: '2,600 guests', price: 'From $6,200 per guest', tags: ['Transatlantic', 'Iconic', 'White Glove'] },
  { name: 'Ritz-Carlton Yacht Collection — Rivieras', route: 'Barcelona to Rome', nights: '7 nights', line: 'Ritz-Carlton Yacht', guests: '298 guests', price: 'From $13,500 per guest', tags: ['Ultra-luxury', 'Boutique', 'Mediterranean'] },
  { name: 'Explora Journeys — Riviera & Aegean', route: 'Nice to Athens', nights: '12 nights', line: 'Explora Journeys', guests: '922 guests', price: 'From $14,500 per guest', tags: ['Riviera', 'Aegean', 'All-inclusive'] },
  { name: 'Seabourn Norwegian Fjords', route: 'Copenhagen to Bergen', nights: '14 nights', line: 'Seabourn', guests: '600 guests', price: 'From $15,900 per guest', tags: ['Fjords', 'Expedition', 'Scenic'] },
];

const CRUISE_LINES = [
  { name: 'Silversea', desc: 'Ultra-luxury all-suite ships with butler service on every voyage.' },
  { name: 'Regent Seven Seas', desc: 'The most inclusive luxury cruise line — everything included.' },
  { name: 'Seabourn', desc: 'Intimate ships, extraordinary destinations, impeccable service.' },
  { name: 'Explora Journeys', desc: 'MSC\'s ultra-luxury brand — ocean state of mind.' },
  { name: 'Ritz-Carlton Yacht Collection', desc: 'The Ritz-Carlton experience at sea.' },
  { name: 'Cunard', desc: 'The iconic White Star Service since 1839.' },
];

const RELATED_COLLECTIONS = [
  { name: 'Expedition Cruises', desc: 'Antarctica, Arctic, Galápagos and remote wilderness', href: '/polar-expeditions', icon: '🧊' },
  { name: 'River Cruises', desc: 'Danube, Rhine, Mekong, Nile and Amazon', href: '/booking?vertical=cruise', icon: '🌊' },
  { name: 'World Cruises', desc: 'Grand voyages spanning multiple continents', href: '/booking?vertical=cruise', icon: '🌍' },
];

export default function CruisesPage() {
  const router = useRouter();

  return (
    <div className="min-h-screen bg-[var(--background)] text-[var(--foreground)]">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[var(--surface-2)]/95 backdrop-blur-xl border-b border-[var(--border)] h-16 flex items-center px-6">
        <Link href="/" className="text-[var(--primary)] font-bold tracking-widest uppercase mr-8" style={{ fontFamily: 'var(--font-serif)' }}>WorldwayLuxe</Link>
        <div className="flex items-center gap-6 text-sm text-[var(--muted-foreground)]">
          <Link href="/safari" className="hover:text-[var(--primary)] transition-colors">Safari</Link>
          <Link href="/rail" className="hover:text-[var(--primary)] transition-colors">Luxury Rail</Link>
          <Link href="/polar-expeditions" className="hover:text-[var(--primary)] transition-colors">Polar</Link>
          <Link href="/tailor-made" className="hover:text-[var(--primary)] transition-colors">Tailor-Made</Link>
        </div>
        <div className="ml-auto flex items-center gap-3">
          <Link href="/auth/login" className="text-sm text-[var(--muted-foreground)] hover:text-[var(--foreground)] px-4 py-2">Sign In</Link>
          <Link href="/auth/register" className="btn-primary text-sm px-5 py-2 rounded-lg">Join Now</Link>
        </div>
      </nav>
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#050810] via-[var(--background)] to-[#0D1525]" />
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(ellipse 80% 60% at 50% 20%, rgba(201,168,76,0.15) 0%, transparent 70%)' }} />
        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <p className="label-caps mb-3 text-[var(--primary)]">All-inclusive at sea</p>
          <h1 className="text-4xl md:text-6xl font-bold mb-4" style={{ fontFamily: 'var(--font-serif)' }}>
            Luxury Cruises<br />
            <span className="gold-text-gradient">All-Suite at Sea.</span>
          </h1>
          <p className="text-lg text-[var(--muted-foreground)] mb-6 max-w-xl">
            All-suite ships, Michelin-caliber dining and immersive shore programmes with the world&apos;s most celebrated cruise lines — Silversea, Regent Seven Seas, Seabourn, Explora Journeys and Ritz-Carlton Yacht Collection.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            {[
              { icon: '🛏', title: 'All-suite fleet', desc: 'Every voyage aboard suite-only ships with butler service.' },
              { icon: '🏝', title: 'Curated shore programmes', desc: 'Private access excursions unavailable to independent travellers.' },
              { icon: '✅', title: 'All-inclusive', desc: 'Flights, transfers, gratuities, premium beverages and shore excursions included.' },
            ]?.map((f) => (
              <div key={f?.title} className="glass-card rounded-xl p-4 border border-[rgba(201,168,76,0.1)]">
                <div className="text-2xl mb-2">{f?.icon}</div>
                <div className="text-sm font-bold mb-1">{f?.title}</div>
                <div className="text-xs text-[var(--muted-foreground)]">{f?.desc}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-4">
            <button onClick={() => router?.push('/booking?vertical=cruise')} className="btn-primary px-8 py-3 rounded-xl text-sm font-semibold">Enquire Now</button>
            <Link href="/booking?vertical=cruise" className="btn-ghost px-8 py-3 rounded-xl text-sm font-semibold border border-[var(--border)]">Trip Builder</Link>
          </div>
        </div>
      </section>
      {/* Featured Voyages */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Featured Voyages</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>Handpicked <span className="gold-text-gradient">Luxury Voyages</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {FEATURED_VOYAGES?.map((v) => (
              <div key={v?.name} className="glass-card rounded-xl p-5 hover:border-[rgba(201,168,76,0.3)] transition-all group cursor-pointer" onClick={() => router?.push('/booking?vertical=cruise')}>
                <div className="text-2xl mb-3">🛳</div>
                <h3 className="text-sm font-bold mb-1 group-hover:text-[var(--primary)] transition-colors" style={{ fontFamily: 'var(--font-serif)' }}>{v?.name}</h3>
                <p className="text-xs text-[var(--muted-foreground)] mb-1">{v?.route}</p>
                <p className="text-xs text-[var(--muted-foreground)] mb-1">{v?.nights} · {v?.guests}</p>
                <p className="text-xs text-[var(--muted-foreground)] mb-3">{v?.line}</p>
                <div className="flex flex-wrap gap-1 mb-3">
                  {v?.tags?.map((tag) => (
                    <span key={tag} className="text-[10px] px-2 py-0.5 rounded-full bg-[rgba(201,168,76,0.1)] text-[var(--primary)]">{tag}</span>
                  ))}
                </div>
                <p className="text-xs font-semibold text-[var(--primary)]">{v?.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Cruise Lines */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-2">Partner Cruise Lines</p>
          <h2 className="text-2xl font-bold mb-8" style={{ fontFamily: 'var(--font-serif)' }}>The World&apos;s Finest <span className="gold-text-gradient">Luxury Lines</span></h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {CRUISE_LINES?.map((c) => (
              <div key={c?.name} className="glass-card rounded-xl p-4 flex items-start gap-3">
                <span className="text-2xl">🛳</span>
                <div>
                  <div className="text-sm font-bold mb-1">{c?.name}</div>
                  <div className="text-xs text-[var(--muted-foreground)]">{c?.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* FAQ */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-6">FAQ</p>
          <div className="space-y-4">
            {[
              { q: 'What is included in the price?', a: 'Ocean-view suite, all meals, unlimited fine wines & spirits, gratuities, business-class flights on select fares and curated shore excursions.' },
              { q: 'What about single travellers?', a: 'Selected departures waive or reduce the single supplement. Ask our specialists for current availability.' },
              { q: 'How do I design my voyage?', a: 'Our specialists craft a proposal within 72 hours. Contact us or use the Trip Builder to get started.' },
            ]?.map((faq) => (
              <div key={faq?.q} className="glass-card rounded-xl p-5">
                <div className="text-sm font-bold mb-2 text-[var(--primary)]">{faq?.q}</div>
                <div className="text-xs text-[var(--muted-foreground)] leading-relaxed">{faq?.a}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Related Collections */}
      <section className="py-16 border-t border-[var(--border)]">
        <div className="max-w-5xl mx-auto px-6">
          <p className="label-caps mb-6">Related Collections</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {RELATED_COLLECTIONS?.map((c) => (
              <Link key={c?.name} href={c?.href} className="glass-card rounded-xl p-5 hover:border-[rgba(201,168,76,0.3)] transition-all group">
                <div className="text-2xl mb-2">{c?.icon}</div>
                <div className="text-sm font-bold mb-1 group-hover:text-[var(--primary)] transition-colors">{c?.name}</div>
                <div className="text-xs text-[var(--muted-foreground)]">{c?.desc}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <footer className="border-t border-[var(--border)] py-8">
        <div className="max-w-5xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="text-xs text-[var(--muted-foreground)]">© 2026 Worldway Travels Group · All rights reserved.</div>
          <div className="flex items-center gap-6 text-xs text-[var(--muted-foreground)]">
            <Link href="/" className="hover:text-[var(--primary)] transition-colors">Home</Link>
            <Link href="/safari" className="hover:text-[var(--primary)] transition-colors">Safari</Link>
            <Link href="/tailor-made" className="hover:text-[var(--primary)] transition-colors">Tailor-Made</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
